import { formatFollowersCount } from "@/Helpers/format-helper";
import { useGeneralSettings } from "@/Providers/General";
import { useInfluencer } from "@/Providers/Influencer";
import { Button } from "@/shadcnComponents/ui/button";
import { Card, CardContent } from "@/shadcnComponents/ui/card";
import { Label } from "@/shadcnComponents/ui/label";
import { Textarea } from "@/shadcnComponents/ui/textarea";
import { Linkedin, ArrowRight, CheckCircle, Rocket } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { Link, useNavigate } from "react-router-dom";
import { LiveCampaignData } from "../PromoteAd/CampaignInterface/LiveCampaignInterface";
import { useBrand } from "@/Providers/Brand";
import { useToast } from "@/shadcnComponents/ui/use-toast";
import { CreateNewCampaign } from "@/Components/Brand/Sidebar/Sidebar";

export default function CollabWithMePage() {
  const brandState = useBrand();
  const influencerState = useInfluencer();
  const generalState = useGeneralSettings();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [slug, setSlug] = useState("");
  const [cookies, , removeCookie] = useCookies();
  const [TempInfluData, setTempInfluData] = useState<{
    avgLikes: number;
    avgComments: number;
    avgEngagement: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    avgEngagementPercent: any;
    avgReposts: number;
    followers: number;
    topThreeEngagedPosts: [];
    name: string;
    gender: string;
    tagline: string;
    location: string;
    profile_image: string;
    experience: string;
    badge: string;
    featured: [];
    profile_url: string;
    firstThirtyLikes: number[];
    firstThirtyComments: number[];
  }>();
  const [FormData, setFormData] = useState<{
    wantToCollab: string;
    budget: string;
    reasonToConnect: string;
    additionalMessage: string;
  }>({
    wantToCollab: "",
    budget: "",
    reasonToConnect: "",
    additionalMessage: "",
  });
  const [currentPage, setCurrentPage] = useState<"form" | "success">("form");

  const getTempInfluencerData = async () => {
    await influencerState
      .getTempInfluencerLinkedinProfileFromSlug(slug)
      .then((resData) => {
        if (resData.success) {
          // if (!resData?.LinkedinAnalysis?.name) {
          //   generalState.setLoading(false);
          //   return navigate("/influencer/dashboard");
          // }
          setTempInfluData(resData?.LinkedinAnalysis);
          generalState.setLoading(false);
        } else {
          generalState?.setLoading(false);
        }
      })
      .catch(() => {
        generalState?.setLoading(false);
      });
  };

  useEffect(() => {
    if (cookies.unlockCreatorSearch) {
      setSlug(`${cookies.unlockCreatorSearch}`);
      removeCookie("unlockCreatorSearch");
    } else {
      navigate("/brand/dashboard");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    getTempInfluencerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const getInfluencerTier = (followers: number) => {
    if (followers >= 500000) return "Mega";
    if (followers >= 100000) return "Macro";
    if (followers >= 20000) return "Mid-tier";
    if (followers >= 5000) return "Micro";
    return "Nano";
  };

  // Determine chip color based on tier
  const getTierChipColor = (tier: string) => {
    switch (tier) {
      case "Mega":
        return "bg-purple-100 text-purple-800";
      case "Macro":
        return "bg-primary/20 text-primary font-semibold";
      case "Mid-tier":
        return "bg-amber-100 text-amber-800";
      case "Micro":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleSubmit = async () => {
    // Add logic to submit form data to backend
    if (
      !FormData?.reasonToConnect ||
      !FormData?.budget ||
      !FormData?.wantToCollab
    ) {
      return toast({
        title: "Please fill all the required fields",
        variant: "destructive",
      });
    } else {
      // Submit form data
      const result = await brandState?.submitWantToCollabForm({
        ...FormData,
        slug,
      });
      if (result?.success) {
        toast({
          title: "Form submitted successfully",
          variant: "destructive",
        });
        setCurrentPage("success");
      } else {
        toast({
          title: result?.Error,
          variant: "destructive",
        });
      }
    }
  };
  return (
    <div className="min-h-[90vh] bg-white flex flex-1 p-6 pt-0 items-center justify-center">
      {currentPage === "form" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
          {/* Influencer Profile Overview - 3D Card */}
          <div className="w-full max-w-sm perspective-1000">
            <Card className="border-gray-300 rounded-[20px] overflow-hidden w-full transform-gpu transition-all duration-300 hover:translate-y-[-5px] hover:rotate-y-[-2deg] bg-gradient-to-br from-white to-gray-50 shadow-[0_10px_20px_rgba(0,0,0,0.1),0_6px_6px_rgba(0,0,0,0.05),0_0_0_1px_rgba(0,0,0,0.05)]">
              <CardContent className="p-6 relative">
                {/* Subtle 3D highlight effect */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/20 rounded-[20px] pointer-events-none"></div>

                <div className="flex flex-col items-center relative z-10">
                  {/* Profile Photo - removed border */}
                  <div className="relative w-[100px] h-[100px] rounded-full overflow-hidden mb-4 shadow-[0_4px_12px_rgba(0,0,0,0.15)]">
                    <img
                      src={TempInfluData?.profile_image}
                      alt={TempInfluData?.name}
                      className="object-cover w-full h-full"
                    />
                  </div>

                  {/* Name, Tier Chip, and Tagline */}
                  <div className="flex items-center justify-center gap-2 mb-1">
                    <h2 className="text-xl font-bold text-center">
                      {TempInfluData?.name}
                    </h2>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs ${getTierChipColor(
                        getInfluencerTier(TempInfluData?.followers)
                      )}`}
                    >
                      {getInfluencerTier(TempInfluData?.followers)}
                    </span>
                  </div>
                  <p className="text-muted-foreground text-sm text-center line-clamp-1">
                    {TempInfluData?.tagline}
                  </p>

                  {/* LinkedIn and Followers - LinkedIn on left */}
                  <div className="mt-3 flex items-center justify-center space-x-2">
                    <Link
                      to="#"
                      className="text-[#0077b5] hover:text-[#0077b5]/80"
                    >
                      <Linkedin className="h-4 w-4" />
                    </Link>
                    <span className="font-semibold">
                      {formatFollowersCount(TempInfluData?.followers || 0)}
                    </span>
                    <span className="text-muted-foreground text-sm">
                      followers
                    </span>
                  </div>

                  {/* Stats in 2x2 Grid */}
                  <div className="grid grid-cols-2 gap-4 w-full mt-6">
                    {/* Row 1: Avg Likes | Avg Comments */}
                    {TempInfluData?.avgLikes && (
                      <Card className="p-3 bg-white shadow-md transform transition-transform hover:translate-y-[-2px]">
                        <div className="text-center">
                          <p className="text-muted-foreground text-xs">
                            Avg. Likes
                          </p>
                          <p className="font-semibold">
                            {formatFollowersCount(
                              Math.ceil(TempInfluData?.avgLikes)
                            )}
                          </p>
                        </div>
                      </Card>
                    )}

                    {TempInfluData?.avgComments > 0 && (
                      <Card className="p-3 bg-white shadow-md transform transition-transform hover:translate-y-[-2px]">
                        <div className="text-center">
                          <p className="text-muted-foreground text-xs">
                            Avg. Comments
                          </p>
                          <p className="font-semibold">
                            {formatFollowersCount(
                              Math.ceil(TempInfluData?.avgComments)
                            )}
                          </p>
                        </div>
                      </Card>
                    )}

                    {/* Row 2: Avg ER | Total Collab */}
                    {TempInfluData?.avgEngagementPercent > 0 && (
                      <Card className="p-3 bg-white shadow-md transform transition-transform hover:translate-y-[-2px]">
                        <div className="text-center">
                          <p className="text-muted-foreground text-xs">
                            Avg. Engagement Rate
                          </p>
                          <p className="font-semibold">
                            {Math.ceil(
                              TempInfluData?.avgEngagementPercent * 10
                            ) / 10}
                            %
                          </p>
                        </div>
                      </Card>
                    )}

                    {/* <Card className="p-3 bg-white shadow-md transform transition-transform hover:translate-y-[-2px]">
                    <div className="text-center">
                      <p className="text-muted-foreground text-xs">
                        Total Collaborations
                      </p>
                      <p className="font-semibold">{TempInfluData?.pas}</p>
                    </div>
                  </Card> */}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Connect with Influencer Form - 3D Card */}
          <div className="w-full max-w-sm perspective-1000">
            <Card className="border-gray-300 rounded-[20px] overflow-hidden w-full transform-gpu transition-all duration-300 hover:translate-y-[-5px] hover:rotate-y-[2deg] bg-gradient-to-br from-white to-gray-50 shadow-[0_10px_20px_rgba(0,0,0,0.1),0_6px_6px_rgba(0,0,0,0.05),0_0_0_1px_rgba(0,0,0,0.05)]">
              <CardContent className="p-5 relative">
                {/* Subtle 3D highlight effect */}
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/20 rounded-[20px] pointer-events-none"></div>

                <div className="relative z-10">
                  <div className="text-center mb-4">
                    <h2 className="text-lg font-bold">
                      Let’s Get You Connected
                    </h2>
                    <p className="text-muted-foreground text-xs mt-1">
                      But before that, we'd love to know a few quick details
                      about you
                    </p>
                  </div>

                  {/* Form Questions - Stacked Vertically with reduced spacing */}
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <Label htmlFor="collaboration" className="text-xs">
                        Are you looking to collaborate?
                      </Label>
                      <select
                        value={FormData?.wantToCollab}
                        onChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            wantToCollab: value.target.value,
                          }))
                        }
                        id="collaboration"
                        className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background file:border-0 file:bg-transparent file:text-xs file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 shadow-sm"
                      >
                        <option value="">Select an option</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                        <option value="exploring">
                          Simply exploring right now
                        </option>
                      </select>
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="budget" className="text-xs">
                        What’s your campaign budget?
                      </Label>
                      <select
                        value={FormData?.budget}
                        onChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            budget: value.target.value,
                          }))
                        }
                        id="budget"
                        className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1 text-xs ring-offset-background file:border-0 file:bg-transparent file:text-xs file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 shadow-sm"
                      >
                        <option value="">Select a range</option>
                        <option value="<50K">{"<₹50K"}</option>
                        <option value="50K-1L">₹50K to ₹1L</option>
                        <option value="1L-5L">₹1L-₹5L</option>
                        <option value="5L-10L">₹5L-₹10L</option>
                        <option value="10L-30L">₹10L-₹30L</option>
                        <option value=">30L">{">₹30L"}</option>
                      </select>
                    </div>

                    <div className="space-y-1">
                      <Label htmlFor="purpose" className="text-xs">
                        What do you want to connect for?
                      </Label>
                      <Textarea
                        value={FormData?.reasonToConnect}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            reasonToConnect: e.target.value,
                          }))
                        }
                        id="purpose"
                        placeholder="Share the purpose of connecting with the influencer in a few lines"
                        className="min-h-[60px] text-xs px-2 py-1 shadow-sm"
                      />
                    </div>

                    <div className="space-y-1">
                      <Label
                        htmlFor="additional"
                        className="flex items-center gap-1 text-xs"
                      >
                        Additional Message
                        <span className="text-xs text-muted-foreground">
                          (Optional)
                        </span>
                      </Label>
                      <Textarea
                        value={FormData?.additionalMessage}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            additionalMessage: e.target.value,
                          }))
                        }
                        id="additional"
                        placeholder=" Any extra details or specific asks?"
                        className="min-h-[60px] text-xs px-2 py-1 shadow-sm"
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="mt-4 flex flex-col items-center">
                    <Button
                      onClick={handleSubmit}
                      size="sm"
                      className="w-full cursor-pointer text-xs py-1 h-8 shadow-md hover:shadow-lg transition-all transform hover:translate-y-[-2px]"
                      asChild
                    >
                      <span>Submit Details</span>
                    </Button>
                    <p className="text-xs text-muted-foreground mt-1">
                      Our team will connect you with the influencer shortly
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <ConfirmationPage />
      )}
    </div>
  );
}

export function ConfirmationPage() {
  const brandState = useBrand();
  const [sampleCampaign, setSampleCampaign] = useState<LiveCampaignData>();
  const getSampleCampaign = useCallback(async () => {
    const result = await brandState?.getSampleCampaign();
    if (result?.success) {
      setSampleCampaign(result?.collab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    getSampleCampaign();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="flex flex-1 p-6 pt-0 justify-center">
      <div className="w-full max-w-2xl">
        {/* Success Message Section */}
        <Card className="mb-6 overflow-hidden">
          <div className="flex flex-col items-center text-center p-6 bg-green-50">
            {/* Animated tick */}
            <div className="relative mb-3">
              <div className="absolute inset-0 rounded-full animate-ping bg-green-400 opacity-20"></div>
              <CheckCircle className="h-12 w-12 text-green-500 relative z-10" />
            </div>

            {/* Heading */}
            <h2 className="text-2xl font-bold mb-2 text-green-800">
              Request Submitted
            </h2>

            {/* Subheading */}
            <p className="text-sm text-green-700/80 max-w-lg">
              <span className="font-bold">
                We've shared your connection request with the influencer.
              </span>{" "}
              We'll notify you when they respond. Until then, explore more
              influencers and campaigns.
            </p>
          </div>
        </Card>

        {/* Clickable Sample Campaign Section */}
        <Link
          to={`/Brand/DashBoard/AllCampaign/Campaign/${sampleCampaign?._id}/?sampleId=${sampleCampaign?.brandID}`}
          className="block mb-6 perspective-1000"
        >
          <Card
            className="overflow-hidden border-primary/20 
                    transform-gpu transition-all duration-300 
                    hover:shadow-[0_10px_25px_-5px_rgba(0,0,0,0.15),0_8px_10px_-6px_rgba(0,0,0,0.15),0_0_0_1px_rgba(59,130,246,0.3)] 
                    hover:translate-y-[-2px] hover:scale-[1.01] hover:rotate-x-[-1deg] hover:rotate-y-[0.5deg]
                    bg-gradient-to-br from-white to-gray-50
                    hover:bg-gradient-to-br hover:from-gray-50 hover:to-gray-100"
          >
            {/* Subtle 3D highlight effect */}
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-white/20 rounded-xl pointer-events-none"></div>

            <div className="flex flex-col md:flex-row items-center">
              {/* Left: Graph Image - Reduced Height */}
              <div className="w-full md:w-2/5 p-5 flex items-center justify-center relative z-10">
                <div className="w-full max-w-[150px] h-[120px] transition-transform duration-300 group-hover:scale-105">
                  <img
                    src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Container%203-sYduMLeciYYZy0rGaSULu1qGb7FQ3d.png"
                    alt="Campaign Analytics"
                    className="w-full h-full object-contain drop-shadow-sm"
                  />
                </div>
              </div>

              {/* Middle: Explore Sample Campaign with USPs */}
              <div className="w-full md:w-2/5 p-5 relative z-10">
                <h3 className="text-lg font-bold mb-3 text-primary whitespace-nowrap relative inline-block">
                  Explore a Sample Campaign
                  {/* Highlight underline effect */}
                  <span className="absolute bottom-0 left-0 w-full h-[2px] bg-primary/20"></span>
                </h3>
                <div className="space-y-3">
                  <p className="text-sm flex items-center text-muted-foreground">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary mr-2"></span>
                    Real-time performance tracking
                  </p>
                  <p className="text-sm flex items-center text-muted-foreground">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary mr-2"></span>
                    No calls, texts, messages needed
                  </p>
                  <p className="text-sm flex items-center text-muted-foreground">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary mr-2"></span>
                    Clear, deep analytics and ROI
                  </p>
                </div>
              </div>

              {/* Right: Clickable Link Button with Arrow */}
              <div className="w-full md:w-1/5 p-5 flex items-center justify-center relative z-10">
                <div className="flex flex-col items-center text-center">
                  <div
                    className="rounded-full bg-primary/15 p-2.5 hover:bg-primary/30 transition-all duration-300 
                            hover:shadow-md hover:scale-110 mb-2"
                  >
                    <ArrowRight className="h-5 w-5 text-primary" />
                  </div>
                  <span className="text-sm font-medium">Try it</span>
                </div>
              </div>
            </div>
          </Card>
        </Link>

        {/* Bottom Action Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Button
            variant="outline"
            size="lg"
            className="flex items-center justify-center gap-2"
            asChild
          >
            <Link to="/brand/dashboard">
              <Rocket className="h-4 w-4" />
              Return to Dashboard
            </Link>
          </Button>

          <CreateNewCampaign
            size="lg"
            className="flex items-center justify-center"
          />
        </div>
      </div>
    </div>
  );
}
